const BASE_URL =
  process.env.REACT_APP_BACKEND_URL || '/v1/';

export interface ApiResponse<T = unknown> {
  ok: boolean;
  status: number;
  data: T;
  headers: Headers;
}

function clearAuthAndRedirect() {
  [
    'fininteract_token',
    'fininteract_email',
    'fininteract_name',
    'fininteract_phone',
    'fininteract_iso',
    'fininteract_admin_token',
    'fininteract_admin_email',
    'fininteract_impersonating',
  ].forEach(k => localStorage.removeItem(k));
  window.location.href = '/login?expired=1';
}

export async function apiFetch<T = unknown>(
  method: string,
  endpoint: string,
  body?: unknown,
  token?: string
): Promise<ApiResponse<T>> {
  const url = `${BASE_URL.replace(/\/$/, '')}/${endpoint.replace(/^\//, '')}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const init: RequestInit = {
    method,
    headers,
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const response = await fetch(url, init);

  // Expired or invalid token — clear local state and send to login
  if ((response.status === 401 || response.status === 403) && token) {
    clearAuthAndRedirect();
  }

  let data: T;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    data = await response.json();
  } else {
    const text = await response.text();
    data = text as unknown as T;
  }

  return {
    ok: response.ok,
    status: response.status,
    data,
    headers: response.headers,
  };
}

// Separate from apiFetch because FormData needs the browser to set its own multipart
// Content-Type (with boundary) -- a fixed 'application/json' header would break the upload.
export async function apiUpload<T = unknown>(
  endpoint: string,
  file: File,
  token: string
): Promise<ApiResponse<T>> {
  const url = `${BASE_URL.replace(/\/$/, '')}/${endpoint.replace(/^\//, '')}`;
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (response.status === 401 || response.status === 403) {
    clearAuthAndRedirect();
  }

  let data: T;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    data = await response.json();
  } else {
    const text = await response.text();
    data = text as unknown as T;
  }

  return { ok: response.ok, status: response.status, data, headers: response.headers };
}
